/* eslint-disable no-case-declarations */
import {$generateJSONFromSelectedNodes} from '@lexical/clipboard';
import {employeeAtom, loggedInEmployeeAtom} from 'atoms/employee';
import {EmployeeLdap, TeamWithCounts} from 'client';
import {
  decryptRedactedText,
  encryptRedactedText,
} from 'components/DSYS/editor/utils/redactionCrypto';
import {getDefaultStore} from 'jotai';
import {
  $applyNodeReplacement,
  $createRangeSelection,
  $createTextNode,
  $findMatchingParent,
  $getEditor,
  $isBlockElementNode,
  $isDecoratorNode,
  $isElementNode,
  $isTextNode,
  $parseSerializedNode,
  $setSelection,
  DOMConversionMap,
  DOMConversionOutput,
  ElementNode,
  LexicalNode,
  NodeKey,
  RangeSelection,
  SerializedElementNode,
  Spread,
  TextNode,
} from 'lexical';

import styles from './RedactionNode.module.css';

type TeamWithCountsForRedaction = Pick<
  TeamWithCounts,
  'team_id' | 'name' | 'slug' | 'members_count' | 'subteams_count'
>;

type InlineRedactionSnapshotValidation = {
  hasInvalidStructure: boolean;
  invalidTypes: string[];
  sanitizedNodes: any[];
};

type VisibleRedactionSnapshot = {
  serializedNodes: any[];
  serializedNodesJson: string;
  text: string;
};

type InlineRedactionSnapshotData = {
  plainText: string;
  serializedNodes: any[];
};

type InlineRedactionSelectionSegment = {
  blockKey: string;
  startTextNodeKey: NodeKey;
  startOffset: number;
  endTextNodeKey: NodeKey;
  endOffset: number;
};

export type SerializedRedactionNode = Spread<
  {
    className?: string;
    text?: string; // Keep for backward compatibility and fallback display
    serializedNodes?: string; // Encrypted JSON string of serialized nodes
    users_snapshot?: EmployeeLdap[];
    teams_snapshot?: TeamWithCountsForRedaction[];
  },
  SerializedElementNode
>;

export class RedactionNode extends ElementNode {
  __className?: string;
  __text?: string; // Keep for backward compatibility and fallback display
  __serializedNodes?: any[]; // Array of serialized node JSON data
  __usersSnapshot?: EmployeeLdap[];
  __teamsSnapshot?: TeamWithCountsForRedaction[];
  __renderedAccessState?: 'authorized' | 'fallback';

  static getType(): string {
    return 'redaction-mark';
  }

  isInline(): boolean {
    return true;
  }

  static clone(node: RedactionNode): RedactionNode {
    const cloned = new RedactionNode(
      node.__className,
      node.__text,
      node.__serializedNodes ?? undefined,
      node.__usersSnapshot ?? undefined,
      node.__teamsSnapshot ?? undefined,
      node.__key
    );
    cloned.__renderedAccessState = node.__renderedAccessState;
    return cloned;
  }

  constructor(
    className?: string,
    text?: string,
    serializedNodes?: any[],
    usersSnapshot?: EmployeeLdap[],
    teamsSnapshot?: TeamWithCountsForRedaction[],
    key?: NodeKey
  ) {
    super(key);
    this.__className = className;
    this.__text = text;
    this.__serializedNodes = serializedNodes ?? undefined;
    this.__usersSnapshot = usersSnapshot ?? undefined;
    this.__teamsSnapshot = teamsSnapshot ?? undefined;
  }

  hasRedactionAccess(): boolean {
    try {
      const store = getDefaultStore();
      // Use employeeAtom for user_id (same as used when creating redactions)
      const employee = store.get(employeeAtom);
      // Use loggedInEmployeeAtom for team data (only needed for team-based access)
      const loggedInEmployee = store.get(loggedInEmployeeAtom);

      if (!employee?.user_id) {
        return false;
      }

      const currentUserId = employee.user_id;
      const currentUserTeams = loggedInEmployee?.employeeTeams || [];

      // Check if current user is in the allowed users snapshot
      if (
        this.__usersSnapshot &&
        this.__usersSnapshot.some((user) => user.user_id === currentUserId)
      ) {
        return true;
      }

      // Check if current user is in any of the allowed teams snapshot
      if (
        this.__teamsSnapshot &&
        currentUserTeams.some((team) =>
          this.__teamsSnapshot?.some(
            (redactionTeam) => redactionTeam.team_id === team.team_id.toString()
          )
        )
      ) {
        return true;
      }

      // Default to false if not explicitly allowed
      return false;
    } catch (error) {
      // If there's any error accessing the store, default to false
      console.warn('Error accessing loggedInEmployeeAtom:', error);
      return false;
    }
  }

  isCurrentUserOwner(): boolean {
    const store = getDefaultStore();
    // Use employeeAtom (same as used when creating redactions) for consistency
    // loggedInEmployeeAtom may not be populated yet as it loads slower
    const employee = store.get(employeeAtom);
    if (!employee?.user_id) {
      return false;
    }
    return this.__usersSnapshot?.[0]?.user_id === employee.user_id;
  }

  getDecryptedText(): string {
    return this.hasRedactionAccess() ? (this.__text ?? '') : 'REDACTED';
  }

  getUsersSnapshot(): EmployeeLdap[] {
    return this.__usersSnapshot ?? [];
  }

  getTeamsSnapshot(): TeamWithCounts[] {
    return (this.__teamsSnapshot as TeamWithCounts[]) ?? [];
  }

  setUsers(usersSnapshot?: EmployeeLdap[]) {
    const w = this.getWritable();
    if (usersSnapshot) {
      w.__usersSnapshot = usersSnapshot
        ? usersSnapshot.map((u) => ({
            user_id: u.user_id,
            ldap: u.ldap,
            name: u.name,
            is_deactivated: u.is_deactivated,
          }))
        : undefined;
    }
    // Mark as dirty to trigger updateDOM
    w.markDirty();
  }

  setTeams(teamsSnapshot?: TeamWithCountsForRedaction[]) {
    const w = this.getWritable();
    if (teamsSnapshot) {
      w.__teamsSnapshot = teamsSnapshot
        ? teamsSnapshot.map((t) => ({
            team_id: t.team_id,
            name: t.name,
            slug: t.slug,
            members_count: t.members_count,
            subteams_count: t.subteams_count,
          }))
        : undefined;
    }
    // Mark as dirty to trigger updateDOM
    w.markDirty();
  }

  private syncDOMState(span: HTMLElement): void {
    span.classList.add(styles.redacted);
    span.setAttribute('data-redaction-id', this.getKey());

    if (this.__text) {
      span.setAttribute('data-redacted-text', encryptRedactedText(this.__text));
    } else {
      span.removeAttribute('data-redacted-text');
    }

    // Update users snapshot
    if (this.__usersSnapshot && this.__usersSnapshot.length > 0) {
      span.setAttribute('data-users-snapshot', JSON.stringify(this.__usersSnapshot));
    } else {
      span.removeAttribute('data-users-snapshot');
    }

    // Update teams snapshot
    if (this.__teamsSnapshot && this.__teamsSnapshot.length > 0) {
      span.setAttribute('data-teams-snapshot', JSON.stringify(this.__teamsSnapshot));
    } else {
      span.removeAttribute('data-teams-snapshot');
    }
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');
    this.syncDOMState(span);
    return span;
  }

  updateDOM(_prevNode: RedactionNode, dom: HTMLElement): boolean {
    // Keep the existing DOM node mounted so editor overlays anchored to this span
    // don't lose their reference during ordinary text/style edits.
    this.syncDOMState(dom);
    return false;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (domNode.getAttribute('data-lexical-type') !== 'redaction-mark') {
          return null;
        }
        return {
          conversion: (element: HTMLElement): DOMConversionOutput => {
            const textContent = element.textContent || '';
            const node = $createRedactionNode(undefined, textContent);
            return {node};
          },
          priority: 1,
        };
      },
    };
  }

  exportDOM() {
    const span = document.createElement('span');

    span.classList.add(styles.redacted);
    span.setAttribute('data-lexical-type', 'redaction-mark');

    // Use 'after' callback to ensure Lexical doesn't add children
    return {
      element: span,
      after: (
        generatedElement: HTMLElement | Text | DocumentFragment | null | undefined
      ): HTMLElement | Text | DocumentFragment | null | undefined => {
        if (generatedElement instanceof HTMLElement) {
          // Clear all children that Lexical added and set only our text
          // eslint-disable-next-line no-param-reassign
          generatedElement.textContent = this.getDecryptedText();
        }
        return generatedElement;
      },
    };
  }

  getTextContent(): string {
    return this.getDecryptedText();
  }

  private replaceChildren(nextChildren: LexicalNode[]): void {
    const existingChildren = this.getChildren();
    for (const child of existingChildren) {
      child.remove();
    }

    nextChildren.forEach((child) => {
      this.append(child);
    });
  }

  private getVisibleChildrenSnapshot(): VisibleRedactionSnapshot | null {
    if (!this.hasRedactionAccess() || this.__renderedAccessState !== 'authorized') {
      return null;
    }

    const children = this.getChildren();
    const serializedNodes = children.map((child) => child.exportJSON());
    return {
      serializedNodes,
      serializedNodesJson: JSON.stringify(serializedNodes),
      text: children.map((child) => child.getTextContent()).join(''),
    };
  }

  private hasStoredContentForSnapshot(snapshot: VisibleRedactionSnapshot): boolean {
    return (
      JSON.stringify(this.__serializedNodes ?? []) === snapshot.serializedNodesJson &&
      this.__text === snapshot.text &&
      this.__renderedAccessState === 'authorized'
    );
  }

  needsStoredContentSync(): boolean {
    const snapshot = this.getVisibleChildrenSnapshot();
    if (!snapshot) {
      return false;
    }

    return !this.hasStoredContentForSnapshot(snapshot);
  }

  syncStoredContentFromVisibleChildren(): boolean {
    const snapshot = this.getVisibleChildrenSnapshot();
    if (!snapshot || this.hasStoredContentForSnapshot(snapshot)) {
      return false;
    }

    const writable = this.getWritable();
    writable.__serializedNodes = snapshot.serializedNodes;
    writable.__text = snapshot.text;
    writable.__renderedAccessState = 'authorized';
    return true;
  }

  needsAccessRender(): boolean {
    const children = this.getChildren();
    const hasAccess = this.hasRedactionAccess();

    if (hasAccess) {
      return this.__renderedAccessState !== 'authorized' || children.length === 0;
    }

    return this.__renderedAccessState !== 'fallback' || children.length === 0;
  }

  private renderAuthorizedChildrenFromSnapshot(): void {
    const nextChildren: LexicalNode[] = [];

    if (this.__serializedNodes && this.__serializedNodes.length > 0) {
      const {sanitizedNodes, invalidTypes, hasInvalidStructure} = validateInlineRedactionSnapshot(
        this.__serializedNodes
      );
      const nodesToRender = hasInvalidStructure ? sanitizedNodes : this.__serializedNodes;

      if (hasInvalidStructure) {
        console.warn('[RedactionNode] Sanitized invalid inline redaction snapshot', {
          redactionKey: this.getKey(),
          invalidTypes,
        });
        this.getWritable().__serializedNodes =
          sanitizedNodes.length > 0 ? sanitizedNodes : undefined;
      }

      if (nodesToRender.length > 0) {
        nodesToRender.forEach((serializedNode) => {
          try {
            const recreatedNode = this.recreateNodeFromSerialized(serializedNode);
            if (recreatedNode) {
              nextChildren.push(recreatedNode);
            }
          } catch (error) {
            console.warn('Failed to recreate node from serialized data:', serializedNode, error);
          }
        });
      } else {
        nextChildren.push($createTextNode(this.getDecryptedText()));
      }
    } else {
      nextChildren.push($createTextNode(this.getDecryptedText()));
    }

    this.replaceChildren(nextChildren);
    this.getWritable().__renderedAccessState = 'authorized';
  }

  private renderFallbackChildren(): void {
    this.replaceChildren([$createTextNode('REDACTED')]);
    this.getWritable().__renderedAccessState = 'fallback';
  }

  unwrapRestoredChildren(): LexicalNode[] {
    if (!this.hasRedactionAccess()) {
      console.warn('[RedactionNode] Refusing to unwrap redaction without access', {
        redactionKey: this.getKey(),
      });
      return [];
    }

    if (this.__renderedAccessState === 'authorized' && this.getChildren().length > 0) {
      this.syncStoredContentFromVisibleChildren();
    } else {
      this.renderAuthorizedChildrenFromSnapshot();
    }

    const latestNode = this.getLatest();
    if (!$isRedactionNode(latestNode)) {
      return [];
    }

    const restoredChildren = latestNode.getChildren();
    if (restoredChildren.length === 0) {
      console.warn(
        '[RedactionNode] Falling back to plaintext restore because no structured children were available',
        {
          redactionKey: latestNode.getKey(),
        }
      );
      const fallbackTextNode = $createTextNode(latestNode.getDecryptedText());
      latestNode.insertBefore(fallbackTextNode);
      latestNode.remove();
      return [fallbackTextNode];
    }

    restoredChildren.forEach((child) => {
      latestNode.insertBefore(child);
    });
    latestNode.remove();
    return restoredChildren;
  }

  renderChildrenForAccess() {
    const children = this.getChildren();
    const hasAccess = this.hasRedactionAccess();
    const renderedAccessState = this.__renderedAccessState;

    if (hasAccess) {
      if (renderedAccessState === 'authorized' && children.length > 0) {
        return;
      }

      this.renderAuthorizedChildrenFromSnapshot();
      return;
    }

    if (renderedAccessState === 'fallback' && children.length > 0) {
      return;
    }

    this.renderFallbackChildren();
  }

  private recreateNodeFromSerialized(serializedNode: any): LexicalNode | null {
    try {
      return $parseSerializedNode(serializedNode);
    } catch (error) {
      console.error('Error recreating node:', error);
      return null;
    }
  }

  exportJSON(): SerializedRedactionNode {
    return {
      ...super.exportJSON(),
      type: 'redaction-mark',
      version: 1,
      className: this.__className,
      text: this.__text ? encryptRedactedText(this.__text) : undefined,
      serializedNodes: this.__serializedNodes
        ? encryptRedactedText(JSON.stringify(this.__serializedNodes))
        : undefined,
      users_snapshot: this.__usersSnapshot,
      teams_snapshot: this.__teamsSnapshot,
    };
  }

  static importJSON(serializedNode: SerializedRedactionNode): RedactionNode {
    const decryptedText = serializedNode.text
      ? decryptRedactedText(serializedNode.text)
      : undefined;

    let decryptedSerializedNodes: any[] | undefined;
    if (serializedNode.serializedNodes) {
      try {
        const decryptedNodesJson = decryptRedactedText(serializedNode.serializedNodes);
        decryptedSerializedNodes = JSON.parse(decryptedNodesJson);
      } catch (error) {
        console.warn('Failed to decrypt/parse serialized nodes:', error);
        decryptedSerializedNodes = undefined;
      }
    }

    return new RedactionNode(
      serializedNode.className,
      decryptedText,
      decryptedSerializedNodes,
      serializedNode.users_snapshot,
      serializedNode.teams_snapshot
    );
  }
}

export function $createRedactionNode(
  className?: string,
  text?: string,
  serializedNodes?: any[],
  usersSnapshot?: EmployeeLdap[],
  teamsSnapshot?: TeamWithCounts[]
): RedactionNode {
  return $applyNodeReplacement(
    new RedactionNode(className, text, serializedNodes, usersSnapshot, teamsSnapshot)
  );
}

export const $wrapSelectionInRedactionNode = (
  selection: RangeSelection,
  className?: string,
  _text?: string, // ignore external text; derive from selection
  usersSnapshot?: EmployeeLdap[],
  teamsSnapshot?: TeamWithCounts[]
): RedactionNode | null => {
  const snapshot = buildInlineRedactionSnapshotFromSelection(selection);
  if (!snapshot) {
    return null;
  }

  return insertInlineRedactionFromSelection(
    selection,
    snapshot,
    className,
    usersSnapshot,
    teamsSnapshot
  );
};

export const $unwrapRedactionNode = (node: RedactionNode): LexicalNode[] => {
  return node.unwrapRestoredChildren();
};

export const $wrapSelectionInRedactionNodes = (
  selection: RangeSelection,
  className?: string,
  usersSnapshot?: EmployeeLdap[],
  teamsSnapshot?: TeamWithCounts[]
): RedactionNode[] => {
  const segments = getInlineRedactionSelectionSegments(selection);
  if (segments.length === 0) {
    const node = $wrapSelectionInRedactionNode(
      selection,
      className,
      undefined,
      usersSnapshot,
      teamsSnapshot
    );
    return node ? [node] : [];
  }

  const preparedSegments = segments.map((segment) => {
    const segmentSelection = createSelectionForInlineRedactionSegment(segment);
    const snapshot = buildInlineRedactionSnapshotFromSelection(segmentSelection);
    if (!snapshot) {
      return null;
    }

    return {segment, snapshot};
  });

  if (preparedSegments.some((segment) => segment === null)) {
    return [];
  }

  const redactionNodes: RedactionNode[] = [];

  for (let index = preparedSegments.length - 1; index >= 0; index -= 1) {
    const preparedSegment = preparedSegments[index];
    if (!preparedSegment) {
      continue;
    }

    const segmentSelection = createSelectionForInlineRedactionSegment(preparedSegment.segment);
    const node = insertInlineRedactionFromSelection(
      segmentSelection,
      preparedSegment.snapshot,
      className,
      usersSnapshot,
      teamsSnapshot
    );

    redactionNodes.unshift(node);
  }

  return redactionNodes;
};

function buildInlineRedactionSnapshotFromSelection(
  selection: RangeSelection
): InlineRedactionSnapshotData | null {
  const plainText = selection.getTextContent();
  const serializedNodes = $generateJSONFromSelectedNodes($getEditor(), selection).nodes;
  const {invalidTypes, hasInvalidStructure, sanitizedNodes} =
    validateInlineRedactionSnapshot(serializedNodes);

  if (hasInvalidStructure) {
    if (sanitizedNodes.length === 0) {
      console.warn('[RedactionNode] Refusing to wrap structural content in inline redaction', {
        invalidTypes,
      });
      return null;
    }

    console.warn('[RedactionNode] Sanitized structural selection before inline redaction wrap', {
      invalidTypes,
    });

    return {
      plainText,
      serializedNodes: sanitizedNodes,
    };
  }

  return {
    plainText,
    serializedNodes,
  };
}

function insertInlineRedactionFromSelection(
  selection: RangeSelection,
  snapshot: InlineRedactionSnapshotData,
  className?: string,
  usersSnapshot?: EmployeeLdap[],
  teamsSnapshot?: TeamWithCounts[]
): RedactionNode {
  const node = $createRedactionNode(
    className,
    snapshot.plainText,
    snapshot.serializedNodes,
    usersSnapshot,
    teamsSnapshot
  );
  node.renderChildrenForAccess();

  selection.insertNodes([node]);
  return node;
}

function getInlineRedactionSelectionSegments(
  selection: RangeSelection
): InlineRedactionSelectionSegment[] {
  const selectedTextNodes = selection
    .getNodes()
    .filter((node): node is TextNode => $isTextNode(node) && node.getTextContentSize() > 0);

  if (selectedTextNodes.length === 0) {
    return [];
  }

  const startPoint = selection.isBackward() ? selection.focus : selection.anchor;
  const endPoint = selection.isBackward() ? selection.anchor : selection.focus;
  const segments: InlineRedactionSelectionSegment[] = [];

  let currentBlockKey: string | null = null;
  let currentStartTextNode: TextNode | null = null;
  let currentEndTextNode: TextNode | null = null;

  const pushCurrentSegment = () => {
    if (!currentStartTextNode || !currentEndTextNode || !currentBlockKey) {
      return;
    }

    const startOffset =
      startPoint.type === 'text' && currentStartTextNode.getKey() === startPoint.key
        ? startPoint.offset
        : 0;
    const endOffset =
      endPoint.type === 'text' && currentEndTextNode.getKey() === endPoint.key
        ? endPoint.offset
        : currentEndTextNode.getTextContentSize();

    if (currentStartTextNode.getKey() === currentEndTextNode.getKey() && startOffset >= endOffset) {
      return;
    }

    segments.push({
      blockKey: currentBlockKey,
      startTextNodeKey: currentStartTextNode.getKey(),
      startOffset,
      endTextNodeKey: currentEndTextNode.getKey(),
      endOffset,
    });
  };

  for (const textNode of selectedTextNodes) {
    const block = getNearestInlineRedactionBlock(textNode);
    const blockKey = block?.getKey() ?? textNode.getKey();

    if (blockKey !== currentBlockKey) {
      pushCurrentSegment();
      currentBlockKey = blockKey;
      currentStartTextNode = textNode;
    }

    currentEndTextNode = textNode;
  }

  pushCurrentSegment();
  return segments;
}

function getNearestInlineRedactionBlock(node: LexicalNode): LexicalNode | null {
  if ($isBlockElementNode(node)) {
    return node;
  }

  return $findMatchingParent(node, (parentNode) => $isBlockElementNode(parentNode));
}

function createSelectionForInlineRedactionSegment(
  segment: InlineRedactionSelectionSegment
): RangeSelection {
  const selection = $createRangeSelection();
  selection.anchor.set(segment.startTextNodeKey, segment.startOffset, 'text');
  selection.focus.set(segment.endTextNodeKey, segment.endOffset, 'text');
  $setSelection(selection);
  return selection;
}

function validateInlineRedactionSnapshot(
  serializedNodes: any[]
): InlineRedactionSnapshotValidation {
  const invalidTypes = new Set<string>();
  const sanitizedNodes = serializedNodes.flatMap((serializedNode) =>
    sanitizeSerializedNodeForInlineRedaction(serializedNode, invalidTypes)
  );

  return {
    hasInvalidStructure: invalidTypes.size > 0,
    invalidTypes: [...invalidTypes],
    sanitizedNodes,
  };
}

function sanitizeSerializedNodeForInlineRedaction(
  serializedNode: any,
  invalidTypes: Set<string>
): any[] {
  let parsedNode: LexicalNode | null = null;

  try {
    parsedNode = $parseSerializedNode(serializedNode);
  } catch {
    invalidTypes.add(getSerializedNodeType(serializedNode));
    return unwrapInvalidSerializedNode(serializedNode, invalidTypes);
  }

  if (!isInlineRedactionSafeNode(parsedNode)) {
    invalidTypes.add(parsedNode.getType());
    return unwrapInvalidSerializedNode(serializedNode, invalidTypes);
  }

  if ($isElementNode(parsedNode) && Array.isArray(serializedNode?.children)) {
    const sanitizedChildren = serializedNode.children.flatMap((child: any) =>
      sanitizeSerializedNodeForInlineRedaction(child, invalidTypes)
    );

    if (sanitizedChildren.length === 0) {
      return [];
    }

    return [{...serializedNode, children: sanitizedChildren}];
  }

  return [serializedNode];
}

function unwrapInvalidSerializedNode(serializedNode: any, invalidTypes: Set<string>): any[] {
  if (Array.isArray(serializedNode?.children)) {
    const sanitizedChildren = serializedNode.children.flatMap((child: any) =>
      sanitizeSerializedNodeForInlineRedaction(child, invalidTypes)
    );

    if (sanitizedChildren.length > 0) {
      return sanitizedChildren;
    }
  }

  const textContent = getSerializedNodeTextContent(serializedNode);
  return textContent ? [$createTextNode(textContent).exportJSON()] : [];
}

function isInlineRedactionSafeNode(node: LexicalNode): boolean {
  if ($isTextNode(node)) {
    return true;
  }

  if ($isDecoratorNode(node)) {
    return false;
  }

  if ($isElementNode(node)) {
    return node.isInline();
  }

  return true;
}

function getSerializedNodeType(serializedNode: any): string {
  return typeof serializedNode?.type === 'string' ? serializedNode.type : 'unknown';
}

function getSerializedNodeTextContent(serializedNode: any): string {
  if (!serializedNode || typeof serializedNode !== 'object') {
    return '';
  }

  if (typeof serializedNode.text === 'string') {
    return serializedNode.text;
  }

  if (Array.isArray(serializedNode.children)) {
    return serializedNode.children
      .map((child: any) => getSerializedNodeTextContent(child))
      .join('');
  }

  return '';
}

export function $isRedactionNode(node: LexicalNode | null | undefined): node is RedactionNode {
  return node instanceof RedactionNode;
}
